import React, { useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import DeleteAccountSection from './account/DeleteAccountSection';
import { buildMyDataReport } from './export/myDataReportBuilder';
import logger from '../utils/logger';
import { getToken } from '../utils/tokenManager';
import { usePrivacyPrefs } from '../contexts/PrivacyPrefsContext';
import { resolveFilenamePrefix } from '../constants/privacyPresets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountDataManagement = ({ onAccountDeleted }) => {
  const [downloading, setDownloading] = useState(false);
  const { prefs } = usePrivacyPrefs();
  const headers = { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' };

  const fetchData = async () => {
    const res = await fetch(`${API_URL}/api/auth/download-my-data`, { headers });
    if (!res.ok) throw new Error('Failed to download data');
    return res.json();
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const data = await fetchData();
      const html = buildMyDataReport(data);
      const printWindow = window.open('', '_blank');
      if (!printWindow) { toast.error('Please allow popups to download your report'); return; }
      printWindow.document.write(html);
      printWindow.document.close();
      toast.success('Your data report is ready');
    } catch (err) { logger.error(err); toast.error(err.message || 'Network error'); }
    finally { setDownloading(false); }
  };

  const handleDownloadRaw = async () => {
    setDownloading(true);
    try {
      const data = await fetchData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const prefix = resolveFilenamePrefix(prefs);
      a.download = `${prefix}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Raw data downloaded');
    } catch (err) { logger.error(err); toast.error(err.message || 'Network error'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-6 mt-6">
      <h3 className="text-lg font-bold text-slate-900">Your Data</h3>
      <div className="border border-slate-200 rounded-xl p-4 bg-white">
        <div>
          <p className="font-semibold text-slate-900 text-sm">Download My Data</p>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">Export all your account information, investments, and login history.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button data-testid="download-my-data-btn" onClick={handleDownloadReport} disabled={downloading} variant="outline" size="sm">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              {downloading ? 'Preparing...' : 'Download Report (PDF)'}
            </Button>
            <Button data-testid="download-my-data-raw-btn" onClick={handleDownloadRaw} disabled={downloading} variant="ghost" size="sm" className="text-slate-500">
              <Download className="w-4 h-4 mr-2" />Raw Data (JSON)
            </Button>
          </div>
        </div>
      </div>
      <DeleteAccountSection onAccountDeleted={onAccountDeleted} />
    </div>
  );
};

export default AccountDataManagement;
